import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useSearch, useMenu } from '../../hooks';
import { formatDate } from '../../utils/helpers';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchOpen, setSearchOpen, mobileMenuOpen, setMobileMenuOpen } = useApp();
  const { query, results, loading, search, clearSearch } = useSearch();
  const { data: menuData } = useMenu();
  const allItems = menuData?.data || [];
  const topLevel = allItems.filter(m => !m.parentId).sort((a, b) => a.order - b.order);
  const subMap = {};
  allItems.filter(m => m.parentId).forEach(m => {
    if (!subMap[m.parentId]) subMap[m.parentId] = [];
    subMap[m.parentId].push(m);
  });
  const navLinks = topLevel.map(m => ({
    id: m.id,
    label: m.label,
    path: m.path,
    isExternal: m.isExternal,
    subCats: (subMap[m.id] || []).sort((a, b) => a.order - b.order).map(s => ({
      label: s.label,
      path: s.path,
      isExternal: s.isExternal,
    })),
  }));
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [navVisible, setNavVisible] = useState(false);
  const [clock, setClock] = useState(() => {
    const now = new Date();
    return `${now.toLocaleDateString('en-US', { weekday: 'long' })}, ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  });

  useEffect(() => {
    const id = setInterval(() => {
      const now = new Date();
      setClock(`${now.toLocaleDateString('en-US', { weekday: 'long' })}, ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`);
    }, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    const onScroll = () => setNavVisible(window.scrollY > 90);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location, setMobileMenuOpen, setSearchOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      clearSearch();
    }
  };

  const handleResultClick = (id) => {
    navigate(`/article/${(id || '').slice(0, 8)}`);
    setSearchOpen(false);
    clearSearch();
  };

  return (
    <>
      {/* Main Header */}
      <header className="header">
        <div className="container header-main">
          {/* Left: clock */}
          <div className="header-clock">{clock}</div>

          {/* Center: logo */}
          <div className="logo-section">
            <Link to="/" className="logo">
              <img src="/kbn-logo.png" alt="KBN News" className="logo-img" />
            </Link>
          </div>

          {/* Right: Desktop Actions */}
          <div className="header-actions">
            <button
              className="icon-btn"
              onClick={() => { setSearchOpen(!searchOpen); setMobileMenuOpen(false); }}
              aria-label="Search"
            >
              <SearchIcon />
            </button>
            <a href="/admin/login" className="staff-login-btn">Staff Login</a>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to day mode' : 'Switch to night mode'}
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              <span className="theme-toggle-label">{theme === 'dark' ? 'Day' : 'Night'}</span>
            </button>
          </div>

          {/* Mobile Controls */}
          <div className="header-mobile-controls">
            <button className="icon-btn" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search">
              <SearchIcon />
            </button>
            <button
              className={`hamburger ${mobileMenuOpen ? 'hamburger--open' : ''}`}
              onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setSearchOpen(false); }}
              aria-label="Menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* Navigation + Inline Search */}
        <nav className={`header-nav${navVisible ? ' header-nav--visible' : ''}${searchOpen ? ' header-nav--search' : ''}`}>
          <div className="container" style={{ position: 'relative' }}>
            {searchOpen ? (
              <div className="inline-search">
                <form onSubmit={handleSearchSubmit} className="inline-search-form">
                  <SearchIcon className="inline-search-icon" />
                  <input
                    type="text"
                    className="inline-search-input"
                    placeholder="Search articles, topics, authors…"
                    value={query}
                    onChange={e => search(e.target.value)}
                    autoFocus
                  />
                  {query && (
                    <button type="button" className="inline-search-clear" onClick={clearSearch}>✕</button>
                  )}
                  <button type="button" className="inline-search-close" onClick={() => { setSearchOpen(false); clearSearch(); }}>Close</button>
                </form>
                {query.length >= 2 && (
                  <div className="inline-search-results">
                    {loading && <div className="search-loading">Searching…</div>}
                    {!loading && results.length === 0 && (
                      <div className="search-empty">No results for "{query}"</div>
                    )}
                    {!loading && results.map(article => (
                      <button
                        key={article.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(article.id)}
                      >
                        <span className="search-result-category">{article.category || article.categoryName}</span>
                        <span className="search-result-title">{article.title}</span>
                        <span className="search-result-meta">{article.readTime || article.readingTime} min read</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <ul className="nav-list">
                {navLinks.map(link => (
                  <li key={link.id} className="nav-item">
                    {link.isExternal ? (
                      <a href={link.path} className="nav-link" target="_blank" rel="noreferrer">{link.label}</a>
                    ) : (
                      <Link to={link.path} className={`nav-link ${location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path)) ? 'nav-link--active' : ''}`}>
                        {link.label}
                        {link.subCats.length > 0 && <span className="nav-link-chevron">▾</span>}
                      </Link>
                    )}
                    {link.subCats.length > 0 && (
                      <div className="nav-dropdown">
                        {link.subCats.map((sub, i) => (
                          sub.isExternal
                            ? <a key={i} href={sub.path} className="nav-dropdown-item" target="_blank" rel="noreferrer">{sub.label}</a>
                            : <Link key={i} to={sub.path} className="nav-dropdown-item">{sub.label}</Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'mobile-menu--open' : ''}`}>
        <ul className="mobile-nav-list">
          <li><Link to="/" className="mobile-nav-link">Home</Link></li>
          {navLinks.map(link => (
            <React.Fragment key={link.id}>
              <li>
                {link.isExternal
                  ? <a href={link.path} className="mobile-nav-link" target="_blank" rel="noreferrer">{link.label}</a>
                  : <Link to={link.path} className="mobile-nav-link">{link.label}</Link>}
              </li>
              {link.subCats.map((sub, i) => (
                <li key={i}>
                  {sub.isExternal
                    ? <a href={sub.path} className="mobile-nav-link mobile-nav-sub" target="_blank" rel="noreferrer">↳ {sub.label}</a>
                    : <Link to={sub.path} className="mobile-nav-link mobile-nav-sub">↳ {sub.label}</Link>}
                </li>
              ))}
            </React.Fragment>
          ))}
          <li><Link to="/about" className="mobile-nav-link">About</Link></li>
          <li>
            <button className="mobile-nav-link mobile-theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              {theme === 'dark' ? 'Day Mode' : 'Night Mode'}
            </button>
          </li>
        </ul>
      </div>
    </>
  );
};

const SearchIcon = ({ className = '' }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

export default Header;
